// Report exports (UI design §3.1: "CSV/Excel export"). A report is rows + columns; the same definition makes the
// on-screen table, the CSV and the xlsx. Text that a spreadsheet would run as a formula (= + - @, tab, CR at the
// start — asset descriptions and notes are typed by users) is written with a leading apostrophe in CSV; in xlsx
// every text cell is a string cell, never a formula.

export interface Column<R> {
  header: string;
  value(row: R): string | number | null | undefined;
  /** xlsx number format, e.g. '#,##0.00' */
  numFmt?: string;
  width?: number;
}

const FORMULA_START = /^[=+\-@\t\r]/;

export function csvCell(v: string | number | null | undefined): string {
  if (v == null) return '';
  if (typeof v === 'number') return String(v);
  const text = FORMULA_START.test(v) ? `'${v}` : v;
  return /[",\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

/** RFC 4180 CSV with a UTF-8 BOM so Excel opens BM / 中文 text correctly. */
export function toCsv<R>(rows: R[], columns: Column<R>[]): string {
  const lines = [
    columns.map((c) => csvCell(c.header)).join(','),
    ...rows.map((r) => columns.map((c) => csvCell(c.value(r))).join(',')),
  ];
  return '﻿' + lines.join('\r\n') + '\r\n';
}

export async function toXlsx<R>(
  sheet: string,
  rows: R[],
  columns: Column<R>[],
  heading: string[] = [],
): Promise<Uint8Array> {
  const { default: ExcelJS } = await import('exceljs');
  const wb = new ExcelJS.Workbook();
  wb.creator = 'Fleetspoke Assets';
  wb.created = new Date();
  const ws = wb.addWorksheet(sheet.slice(0, 31)); // Excel's sheet-name limit
  heading.forEach((line, i) => {
    const r = ws.addRow([line]);
    if (i === 0) r.font = { bold: true, size: 14 };
  });
  if (heading.length) ws.addRow([]);
  const header = ws.addRow(columns.map((c) => c.header));
  header.font = { bold: true };
  header.eachCell((cell) => {
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE8EEF4' } };
  });
  for (const r of rows) ws.addRow(columns.map((c) => c.value(r) ?? null));
  columns.forEach((c, i) => {
    const col = ws.getColumn(i + 1);
    col.width = c.width ?? Math.min(40, Math.max(10, c.header.length + 2));
    if (c.numFmt) col.numFmt = c.numFmt;
  });
  ws.views = [{ state: 'frozen', ySplit: header.number }];
  return new Uint8Array(await wb.xlsx.writeBuffer());
}

/** "missing_2026-09-12.csv" — ASCII only, so every browser and OS keeps the name. */
export function reportFileName(report: string, stamp: string, ext: 'csv' | 'xlsx'): string {
  return `${report.replace(/[^\w-]+/g, '-')}_${stamp}.${ext}`;
}
